import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../../core/common/dataTable/index";
import {
  getAllRegistration,
  scheduleInterview,
  updateRegistrationStatus,
} from "../../../../service/registration";
import { all_routes } from "../../../router/all_routes";
import { TableData } from "../../../../core/data/interface";
import { useLocation } from "react-router-dom";
import { formatDate } from "../../../../utils/dateutils";

interface RegistrationData extends TableData {
  registrationNumber: string;
  firstName: string;
  gender: string;
  status: string;
  createdAt: string;
  dateOfBirth: string;
  fatherEmail: string;
  fatherPhoneNumber: string;
  _id: string;
  lastName: string;
  studentPicture: string;
  academicYear: string;
  testStatus?: string;
  interviewDate?: string;
  interviewTime?: string;
}

const TestGroupRegistrations = () => {
  const route = all_routes;
  const [data, setData] = useState<RegistrationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [interviewForm, setInterviewForm] = useState({
    interviewDate: "",
    interviewTime: "",
  });
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [paginationData, setPaginationData] = useState<any>();
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [selectedRegistration, setSelectedRegistration] =
    useState<RegistrationData | null>(null);
  const location = useLocation();
  const stateGroupId = location.state?.groupId;
  const groupId = stateGroupId;

  // Improved time format conversion
  const to12Hour = (time: string) => {
    if (!time || typeof time !== "string") return "";

    // Remove seconds if present
    const timeParts = time.split(":");
    if (timeParts.length < 2) return "";

    let hours = parseInt(timeParts[0]);
    const minutes = parseInt(timeParts[1]);

    if (isNaN(hours)) hours = 0;
    if (isNaN(minutes)) return "";

    const period = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12

    return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  // Format date for input[type="date"]
  const formatDateForInput = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const fetchRegistrations = async () => {
    if (!groupId) {
      console.warn("Group ID missing");
      return;
    }
    setLoading(true);
    try {
      const response = await getAllRegistration({
        ...pagination,
        testGroup: groupId,
        // This screen answers "who has this test group actually used" —
        // needs everyone who ever took a seat, including candidates who've
        // since been admitted (excluded by default everywhere else).
        includeAdmitted: true,
      });
      setData(response.registrations);
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Error fetching registrations:", error);
      toast.error("Failed to fetch registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (groupId) {
      fetchRegistrations();
    }
  }, [groupId, pagination]);

  const handleStatusUpdate = async (
    registrationId: string,
    status: "passed" | "failed"
  ) => {
    try {
      await updateRegistrationStatus(registrationId, status);
      toast.success("Status Updated successfully");
      fetchRegistrations();
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to update status");
    }
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleInterviewFormChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setInterviewForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInterviewSubmit = async () => {
    if (!interviewForm.interviewDate || !interviewForm.interviewTime) {
      toast.error("Please select both date and time");
      return;
    }
    if (!selectedRegistration) {
      toast.error("No candidate selected");
      return;
    }

    try {
      setLoading(true);

      // Format time to HH:mm (24-hour format)
      const timeParts = interviewForm.interviewTime.split(":");
      const formattedTime = `${timeParts[0].padStart(
        2,
        "0"
      )}:${timeParts[1].padStart(2, "0")}`;

      const response = await scheduleInterview(selectedRegistration._id, {
        interviewDate: interviewForm.interviewDate,
        interviewTime: formattedTime,
      });

      toast.success(response.message);
      await fetchRegistrations();
      setShowInterviewModal(false);
      setInterviewForm({
        interviewDate: "",
        interviewTime: "",
      });
    } catch (error: any) {
      console.error("Error scheduling interview:", error);
      toast.error(
        error.response?.data?.message || "Failed to schedule interview"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenInterviewModal = (record: RegistrationData) => {
    setSelectedRegistration(record);
    setInterviewForm({
      interviewDate: record.interviewDate
        ? formatDateForInput(record.interviewDate)
        : "",
      interviewTime: record.interviewTime || "",
    });
    setShowInterviewModal(true);
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "registrationNumber",
      render: (text: string) => (
        <Link to="#" className="link-primary">
          {text}
        </Link>
      ),
    },
    {
      title: "Student Name",
      dataIndex: "firstName",
      render: (_text: string, record: RegistrationData) => (
        <span>{`${record.firstName} ${record.lastName}`}</span>
      ),
    },
    {
      title: "Email",
      dataIndex: "fatherEmail",
    },
    {
      title: "Phone",
      dataIndex: "fatherPhoneNumber",
    },
    {
      title: "Status",
      dataIndex: "testStatus",
      render: (status: string) => (
        <span
          className={`badge badge-soft-${
            status === "passed" ? "success" : "danger"
          }`}
        >
          {status || "Pending"}
        </span>
      ),
    },
    {
      title: "Interview Date/Time",
      dataIndex: "interviewDate",
      render: (date: string, record: RegistrationData) => {
        if (!date && !record.interviewTime) return <span>Not Scheduled</span>;
        return (
          <span>
            {formatDate(date)}{" "}
            {record.interviewTime && to12Hour(record.interviewTime)}
          </span>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_text: string, record: RegistrationData) => (
        <div className="d-flex gap-2">
          {record.testStatus === "passed" ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleOpenInterviewModal(record)}
            >
              {record.interviewDate
                ? "Edit Interview Schedule"
                : "Schedule Interview"}
            </button>
          ) : record.testStatus === "failed" ? (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => handleStatusUpdate(record._id, "passed")}
            >
              Promote
            </button>
          ) : (
            <>
              <button
                className={`btn btn-sm ${
                  record.testStatus === "passed"
                    ? "btn-success"
                    : "btn-outline-success"
                }`}
                onClick={() => handleStatusUpdate(record._id, "passed")}
              >
                Pass
              </button>
              <button
                className={`btn btn-sm ${
                  record.testStatus === "fail"
                    ? "btn-danger"
                    : "btn-outline-danger"
                }`}
                onClick={() => handleStatusUpdate(record._id, "failed")}
              >
                Fail
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  const dateRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () =>
    dateRef.current?.showPicker?.() || dateRef.current?.focus();
  const openTimePicker = () =>
    timeRef.current?.showPicker?.() || timeRef.current?.focus();

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto">
            <h3 className="page-title mb-1">Test Group Registrations</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={route.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={route.testGroup}>Test Groups</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Registrations
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0 py-3">
            <Table
              columns={columns}
              dataSource={data}
              pagination={{ ...paginationData, onChange: handleChange }}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {/* Interview Scheduling Modal */}
      {showInterviewModal && selectedRegistration && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedRegistration.interviewDate
                    ? "Edit Interview"
                    : "Schedule Interview"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowInterviewModal(false);
                    setInterviewForm({
                      interviewDate: "",
                      interviewTime: "",
                    });
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label" onClick={openDatePicker}>
                    Interview Date*
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      value={interviewForm.interviewDate}
                      onClick={openDatePicker}
                    />
                    <span className="input-group-text" onClick={openDatePicker}>
                      <i className="ti ti-calendar" />
                    </span>
                    <input
                      ref={dateRef}
                      type="date"
                      name="interviewDate"
                      value={interviewForm.interviewDate}
                      onChange={handleInterviewFormChange}
                      required
                      className="position-absolute opacity-0 pointer-events-none"
                      style={{ width: 0, height: 0 }}
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" onClick={openTimePicker}>
                    Interview Time*
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      value={to12Hour(interviewForm.interviewTime)}
                      onClick={openTimePicker}
                    />
                    <span className="input-group-text" onClick={openTimePicker}>
                      <i className="ti ti-clock" />
                    </span>
                    <input
                      ref={timeRef}
                      type="time"
                      name="interviewTime"
                      value={interviewForm.interviewTime}
                      onChange={handleInterviewFormChange}
                      required
                      className="position-absolute opacity-0 pointer-events-none"
                      style={{ width: 0, height: 0 }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowInterviewModal(false);
                    setInterviewForm({
                      interviewDate: "",
                      interviewTime: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleInterviewSubmit}
                  disabled={
                    !interviewForm.interviewDate || !interviewForm.interviewTime
                  }
                >
                  {selectedRegistration.interviewDate ? "Update" : "Schedule"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestGroupRegistrations;
